import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { LabellingRedundancyDailyEntry } from "src/store/api/generatedApi";

type RedundancyChartProps = Readonly<{
  entries: LabellingRedundancyDailyEntry[];
  isLoading: boolean;
}>;

export const RedundancyChart = ({
  entries,
  isLoading,
}: RedundancyChartProps) => {
  const { t } = useTenantTranslation();
  const { data: palette } = useTheme().palette;

  const passColors: Record<number, string> = useMemo(
    () => ({ 1: palette.green, 2: palette.orange, 3: palette.red }),
    [palette]
  );

  const traces: Partial<PlotData>[] = useMemo(() => {
    const labelerCounts = [
      ...new Set(entries.map((entry) => entry.labeller_count)),
    ];
    const sortedCounts = sorted(labelerCounts, (count) => count);

    return sortedCounts.map((labelerCount) => {
      const filtered = sorted(
        entries.filter((entry) => entry.labeller_count === labelerCount),
        (entry) => entry.footage_date
      );
      return {
        x: filtered.map((entry) => entry.footage_date),
        y: filtered.map((entry) => entry.footage_seconds / 3600),
        type: "bar" as const,
        name: `${labelerCount} ${labelerCount === 1 ? t("labeller") : t("labellers")}`,
        marker: {
          color: passColors[labelerCount] ?? palette.pink,
        },
      };
    });
  }, [entries, t, passColors, palette.pink]);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (entries.length === 0) {
    return <Alert severity="info">{t("noDataAvailable")}</Alert>;
  }

  return (
    <Plot
      data={traces}
      layout={{
        autosize: true,
        barmode: "stack",
        xaxis: { type: "date" },
        yaxis: { title: { text: t("footageHours") } },
        margin: { l: 60, r: 30, t: 30, b: 60 },
        hovermode: "x unified",
        plot_bgcolor: "transparent",
        paper_bgcolor: "transparent",
        legend: { orientation: "h", y: -0.2 },
      }}
      config={{
        responsive: true,
        displayModeBar: false,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "300px" }}
      useResizeHandler
    />
  );
};
